import type { SyncEngine } from './engine.ts'
import { onBackOnScreen } from '../platform/visibility.ts'

/** When to sync (ARCHITECTURE 9.11): iOS has no Background Sync, so the app syncs when it opens, when it comes back on screen,
 *  when the network returns, on a timer, and shortly after the user records something. Never relied on to be called.
 *  "Back on screen" is platform/visibility.ts's: the page becoming visible, or the native app becoming active again (its Android shell keeps the
 *  page "visible" in the background, M19b, so the page's own event would never come).
 *  The radio (M23-0, docs/M23.md item 7): the browser's `offline` event marks the engine offline at once, so the badge says "Hors ligne" the moment
 *  the phone loses the network instead of "Tout est envoyé" until a send fails; `online` marks it unknown again (null) and syncs, and only the
 *  server's answer makes it "online". A page opened with the radio already off starts offline the same way (`navigator.onLine`). */
export function startAutoSync(engine: SyncEngine, opts: { intervalMs?: number } = {}): { stop(): void; soon(): void } {
  let timer: ReturnType<typeof setTimeout> | undefined
  let wake: ReturnType<typeof setTimeout> | undefined
  let stopped = false
  // A hidden window still syncs: on a workshop PC a minimised window must not sit on unsent work. Browsers throttle background
  // timers on their own (about once a minute), so the cost is small; the visibility change below only makes it prompt.
  const run = () => { if (!stopped) void engine.sync().then(schedule) }
  const schedule = async () => {
    if (stopped) return
    clearTimeout(wake)
    const at = await engine.nextWakeAt()
    if (at !== null) wake = setTimeout(run, Math.max(500, at - Date.now()))
  }
  const soon = () => { clearTimeout(timer); timer = setTimeout(run, 300) }
  const offline = () => { if (!stopped) engine.setOnline(false) }
  const online = () => { if (stopped) return; engine.setOnline(null); run() }
  const unwatchScreen = onBackOnScreen(run)
  window.addEventListener('online', online)
  window.addEventListener('offline', offline)
  const interval = setInterval(run, opts.intervalMs ?? 60_000)
  if (typeof navigator !== 'undefined' && navigator.onLine === false) offline()
  run()
  return {
    soon,
    stop() {
      stopped = true; clearTimeout(timer); clearTimeout(wake); clearInterval(interval); unwatchScreen()
      window.removeEventListener('online', online); window.removeEventListener('offline', offline)
    },
  }
}
